import { lazy, Suspense, ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import type { Database } from "@shared/database.types";

type AppRole = Database["public"]["Enums"]["app_role"];

// Lazy pages
const Login = lazy(() => import("@/pages/auth/Login"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Tasks = lazy(() => import("@/pages/admin/Tasks"));
const Clients = lazy(() => import("@/pages/admin/Clients"));
const ClientDetail = lazy(() => import("@/pages/admin/ClientDetail"));
const Team = lazy(() => import("@/pages/admin/Team"));
const Requests = lazy(() => import("@/pages/admin/Requests"));
const Reports = lazy(() => import("@/pages/admin/Reports"));
const ActivityPage = lazy(() => import("@/pages/admin/Activity"));
const Subscriptions = lazy(() => import("@/pages/admin/Subscriptions"));
const FinanceOverview = lazy(() => import("@/pages/admin/FinanceOverview"));
const FinanceMovements = lazy(() => import("@/pages/admin/FinanceMovements"));
const FinanceReports = lazy(() => import("@/pages/admin/FinanceReports"));
const FinanceSalaries = lazy(() => import("@/pages/admin/FinanceSalaries"));
const Projects = lazy(() => import("@/pages/admin/Projects"));
const ProjectDetail = lazy(() => import("@/pages/admin/ProjectDetail"));
const Notes = lazy(() => import("@/pages/admin/Notes"));
const Pricing = lazy(() => import("@/pages/admin/Pricing"));
const Quotes = lazy(() => import("@/pages/admin/Quotes"));
const Referrals = lazy(() => import("@/pages/admin/Referrals"));
const Goals = lazy(() => import("@/pages/admin/Goals"));
const Agenda = lazy(() => import("@/pages/admin/Agenda"));
const Boards = lazy(() => import("@/pages/admin/Boards"));
const Board = lazy(() => import("@/pages/admin/Board"));
const FiloRosso = lazy(() => import("@/pages/admin/FiloRosso"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
const PortalTasks = lazy(() => import("@/pages/portal/PortalTasks"));
const PortalRequests = lazy(() => import("@/pages/portal/PortalRequests"));
const PortalReports = lazy(() => import("@/pages/portal/PortalReports"));
const PortalFinance = lazy(() => import("@/pages/portal/PortalFinance"));
const PortalProfile = lazy(() => import("@/pages/portal/PortalProfile"));
const PortalRoadmap = lazy(() => import("@/pages/portal/PortalRoadmap"));
const PortalBrand = lazy(() => import("@/pages/portal/PortalBrand"));
const PortalUploads = lazy(() => import("@/pages/portal/PortalUploads"));

function CenteredLoader() {
  return (
    <div className="flex h-screen items-center justify-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: ReactNode;
  allowedRoles: AppRole[];
}) {
  const { loading, session, roles } = useAuth();

  if (loading) return <CenteredLoader />;
  if (!session) return <Navigate to="/login" replace />;

  if (roles.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center p-8 text-center">
        <div>
          <p className="text-lg font-medium">Accesso in attesa di approvazione</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Il tuo account è autenticato ma nessun ruolo ti è ancora stato assegnato.
            Contatta il super admin della tua agenzia.
          </p>
        </div>
      </div>
    );
  }

  const hasAllowed = roles.some((r) => allowedRoles.includes(r));
  if (!hasAllowed) {
    if (roles.includes("super_admin") || roles.includes("team_member")) {
      return <Navigate to="/admin" replace />;
    }
    if (roles.includes("client_admin") || roles.includes("client_viewer")) {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RoleRedirect({ children }: { children: ReactNode }) {
  const { loading, session, roles } = useAuth();
  if (loading) return null;
  if (!session) return <>{children}</>;

  if (roles.includes("super_admin") || roles.includes("team_member")) {
    return <Navigate to="/admin" replace />;
  }
  if (roles.includes("client_admin") || roles.includes("client_viewer")) {
    return <Navigate to="/portal" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<CenteredLoader />}>
        <Routes>
        <Route
          path="/login"
          element={
            <RoleRedirect>
              <Login />
            </RoleRedirect>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "team_member"]}>
              <AdminLayout>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="clients" element={<Clients />} />
                  <Route path="clients/:id" element={<ClientDetail />} />
                  <Route path="team" element={<Team />} />
                  <Route path="requests" element={<Requests />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="activity" element={<ActivityPage />} />
                  <Route path="subscriptions" element={<Subscriptions />} />
                  <Route path="finance" element={<FinanceOverview />} />
                  <Route path="finance/movements" element={<FinanceMovements />} />
                  <Route path="finance/reports" element={<FinanceReports />} />
                  <Route path="finance/salaries" element={<FinanceSalaries />} />
                  <Route path="projects" element={<Projects />} />
                  <Route path="projects/:id" element={<ProjectDetail />} />
                  <Route path="notes" element={<Notes />} />
                  <Route path="pricing" element={<Pricing />} />
                  <Route path="quotes" element={<Quotes />} />
                  <Route path="referrals" element={<Referrals />} />
                  <Route path="goals" element={<Goals />} />
                  <Route path="agenda" element={<Agenda />} />
                  <Route path="boards" element={<Boards />} />
                  <Route path="boards/:clientId" element={<Board />} />
                  <Route path="filo-rosso" element={<FiloRosso />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/portal/*"
          element={
            <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
              <PortalLayout>
                <Routes>
                  <Route index element={<PortalDashboard />} />
                  <Route path="tasks" element={<PortalTasks />} />
                  <Route path="finance" element={<PortalFinance />} />
                  <Route path="requests" element={<PortalRequests />} />
                  <Route path="reports" element={<PortalReports />} />
                  <Route path="profile" element={<PortalProfile />} />
                  <Route path="roadmap" element={<PortalRoadmap />} />
                  <Route path="brand" element={<PortalBrand />} />
                  <Route path="uploads" element={<PortalUploads />} />
                  <Route path="*" element={<Navigate to="/portal" replace />} />
                </Routes>
              </PortalLayout>
            </ProtectedRoute>
          }
        />

        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
